export const DEVANAGARI_DIGITS = '०१२३४५६७८९';

export const toMarathiDigits = (value = '') => String(value)
  .replace(/\d/g, digit => DEVANAGARI_DIGITS[Number(digit)] ?? digit);

export const toEnglishDigits = (value = '') => String(value)
  .replace(/[०-९]/g, digit => String(DEVANAGARI_DIGITS.indexOf(digit)));

export const formatMarathiNumber = (value = 0, options = {}) => {
  const number = Number(value || 0);
  return toMarathiDigits(number.toLocaleString('en-IN', options));
};

export const formatMarathiCurrency = (value = 0, options = {}) =>
  `₹${formatMarathiNumber(value, options)}`;

export const formatMarathiDate = (value, options = {}) =>
  value ? toMarathiDigits(new Date(value).toLocaleDateString('mr-IN', options)) : '';

export const formatMarathiDateTime = (value, options = {}) =>
  value ? toMarathiDigits(new Date(value).toLocaleString('mr-IN', options)) : '';

const VIRAMA = '्';

const COMMON_WORDS = {
  // Core
  marathi: 'मराठी',
  maharashtra: 'महाराष्ट्र',
  gram: 'ग्राम',
  grampanchayat: 'ग्रामपंचायत',
  panchayat: 'पंचायत',
  gav: 'गाव',
  gaav: 'गाव',
  gaon: 'गाव',
  deulgaon: 'देऊळगाव',
  gada: 'गाडा',
  namaskar: 'नमस्कार',
  test: 'टेस्ट',
  review: 'रिव्ह्यू',
  vyavasay: 'व्यवसाय',
  dukan: 'दुकान',
  hospital: 'हॉस्पिटल',
  office: 'कार्यालय',
  mobile: 'मोबाईल',
  bill: 'बिल',

  // Certificates & Documents
  janm: 'जन्म',
  praman: 'प्रमाण',
  pramanpatra: 'प्रमाणपत्र',
  pramanpatr: 'प्रमाणपत्र',
  patra: 'पत्र',
  mrutyu: 'मृत्यू',
  mrityu: 'मृत्यू',
  vivah: 'विवाह',
  rahivasi: 'रहिवासी',
  utpanna: 'उत्पन्न',
  jat: 'जात',
  arj: 'अर्ज',
  suchana: 'सूचना',
  kagadpatra: 'कागदपत्र',
  kagadpatre: 'कागदपत्रे',
  karyalay: 'कार्यालय',
  karyalaya: 'कार्यालय',
  aadhaar: 'आधार',
  adhar: 'आधार',
  yojana: 'योजना',
  seva: 'सेवा',

  // Administration & Departments
  vibhag: 'विभाग',
  ani: 'आणि',
  nagar: 'नगर',
  nagarsevak: 'नगरसेवक',
  nagarsevika: 'नगरसेविका',
  taluka: 'तालुका',
  jilha: 'जिल्हा',
  rajya: 'राज्य',
  sarkar: 'सरकार',
  shasan: 'शासन',
  gramsevak: 'ग्रामसेवक',
  gramsevika: 'ग्रामसेविका',
  sabha: 'सभा',
  sabhapati: 'सभापती',

  // Common usage
  mitra: 'मित्र',
  mitr: 'मित्र',
  shikshan: 'शिक्षण',
  vidyalay: 'विद्यालय',
  shala: 'शाळा',
  pustak: 'पुस्तक',
  pustake: 'पुस्तके',
  shikshak: 'शिक्षक',
  shikshika: 'शिक्षिका',
  vidyarthi: 'विद्यार्थी',
  vidyarthini: 'विद्यार्थिनी',

  // Government Schemes & Services
  pension: 'पेन्शन',
  nirnay: 'निर्णय',
  arjak: 'अर्जक',
  sahay: 'सहाय',
  sahayata: 'सहायता',
  yadi: 'यादी',
  suchi: 'सूची',
  anudan: 'अनुदान',
  kar: 'कर',
  malmatta: 'मालमत्ता',
  karpatra: 'करपत्र',
  karpatre: 'करपत्रे',

  // Miscellaneous
  ghar: 'घर',
  kutumb: 'कुटुंब',
  kutumbh: 'कुटुंब',
  vyakti: 'व्यक्ती',
  mahiti: 'माहिती',
  prashna: 'प्रश्न',
  uttar: 'उत्तर',
  samasya: 'समस्या',
  nirakaran: 'निराकरण',
};


const VOWELS = {
  aa: { independent: 'आ', sign: 'ा' },
  ai: { independent: 'ऐ', sign: 'ै' },
  au: { independent: 'औ', sign: 'ौ' },
  ee: { independent: 'ई', sign: 'ी' },
  ii: { independent: 'ई', sign: 'ी' },
  oo: { independent: 'ऊ', sign: 'ू' },
  uu: { independent: 'ऊ', sign: 'ू' },
  a: { independent: 'अ', sign: '' },
  i: { independent: 'इ', sign: 'ि' },
  u: { independent: 'उ', sign: 'ु' },
  e: { independent: 'ए', sign: 'े' },
  o: { independent: 'ओ', sign: 'ो' },
};

const CONSONANTS = {
  ksh: 'क्ष',
  dny: 'ज्ञ',
  gny: 'ज्ञ',
  shr: 'श्र',
  chh: 'छ',
  kh: 'ख',
  gh: 'घ',
  ch: 'च',
  jh: 'झ',
  th: 'थ',
  dh: 'ध',
  ph: 'फ',
  bh: 'भ',
  sh: 'श',
  gn: 'ज्ञ',
  gy: 'ज्ञ',
  ny: 'ञ',
  ng: 'ङ',
  k: 'क',
  g: 'ग',
  c: 'क',
  j: 'ज',
  z: 'झ',
  t: 'त',
  d: 'द',
  n: 'न',
  p: 'प',
  f: 'फ',
  b: 'ब',
  m: 'म',
  y: 'य',
  r: 'र',
  l: 'ल',
  w: 'व',
  v: 'व',
  s: 'स',
  h: 'ह',
};

const VOWEL_KEYS = Object.keys(VOWELS).sort((a, b) => b.length - a.length);
const CONSONANT_KEYS = Object.keys(CONSONANTS).sort((a, b) => b.length - a.length);

const matchFrom = (text, index, keys) => keys.find(key => text.startsWith(key, index));

const isAsciiLetter = char => /[a-z]/.test(char);
const isAsciiDigit = char => /\d/.test(char);

const transliterateAsciiToken = token => {
  const lower = token.toLowerCase();
  if (COMMON_WORDS[lower]) return COMMON_WORDS[lower];
  if (/^[A-Z]{2,}$/.test(token)) return token;

  let result = '';
  let index = 0;

  while (index < lower.length) {
    const char = lower[index];

    if (isAsciiDigit(char)) {
      result += toMarathiDigits(char);
      index += 1;
      continue;
    }

    if (!isAsciiLetter(char)) {
      result += token[index];
      index += 1;
      continue;
    }

    const previous = lower[index - 1] || '';
    const nextConsonant = matchFrom(lower, index + 1, CONSONANT_KEYS);
    if ((char === 'n' || char === 'm') && result && nextConsonant && /[aeiou]/.test(previous)) {
      result += 'ं';
      index += 1;
      continue;
    }

    const vowelKey = matchFrom(lower, index, VOWEL_KEYS);
    if (vowelKey) {
      result += VOWELS[vowelKey].independent;
      index += vowelKey.length;
      continue;
    }

    const consonantKey = matchFrom(lower, index, CONSONANT_KEYS);
    if (consonantKey) {
      const consonant = CONSONANTS[consonantKey];
      index += consonantKey.length;

      const followingVowelKey = matchFrom(lower, index, VOWEL_KEYS);
      if (followingVowelKey) {
        result += consonant + VOWELS[followingVowelKey].sign;
        index += followingVowelKey.length;
      } else if (matchFrom(lower, index, CONSONANT_KEYS)) {
        result += consonant + VIRAMA;
      } else {
        result += consonant;
      }
      continue;
    }

    result += token[index];
    index += 1;
  }

  return result;
};

export const phoneticToMarathi = (value = '') => String(value)
  .replace(/[A-Za-z0-9]+/g, token => transliterateAsciiToken(token));
